import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IStockTableViewArticle } from "@/data/model/Article";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { StockTableEditor } from "@/portal/elements/StockTableEditor";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";

export class StockController {
    private $element: JQuery;
    private dao: CatalogDao;
    private dataTable: DataTable<IStockTableViewArticle>;
    private search: TableFilter;
    private editors: Array<StockTableEditor>;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);
        this.editors = new Array<StockTableEditor>();

        //bind export btns
        const exportButton = this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.stock-export");
        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.stock-export").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            exportButton
                .addClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--brand disabled")
                .prop("disabled", true);
            this.dao
                .exportStock({ locationId: TastierGlobal.getLocationSwitch()!.getCurrentLocationId()! })
                .then(() => {})
                .catch((ex) => {
                    logError(ex);
                    UiNotification.showException(i18n("modal.stockExport.failedExport"));
                })
                .finally(() => {
                    exportButton
                        .removeClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--brand disabled")
                        .prop("disabled", false);
                });
        });

        const tableConfig: IDataTableConfig<IStockTableViewArticle> = {
            columnMapping: [
                {
                    name: i18n("general.fields.designation"),
                    property: "internalName",
                    formatter: (m) => {
                        let name = m.internalName;
                        if (name === undefined) name = Format.htmlEscape(translate(m.name));

                        return $('<a href="/article/' + m.id + '">' + name + "</a>");
                    },
                },
                {
                    name: i18n("general.fields.price"),
                    property: "price",
                    styles: { width: "11%" },
                    formatter: (m, p) => {
                        if (m.isOpenPriceEnabled) {
                            return i18n("general.fields.open");
                        } else {
                            return p.toFixed(2) + " CHF";
                        }
                    },
                },
                {
                    name: i18n("general.fields.oohPrice"),
                    property: "oohPrice",
                    styles: { width: "11%" },
                    formatter: (m, p) => {
                        if (m.isOpenPriceEnabled) {
                            return i18n("general.fields.open");
                        } else {
                            return p === undefined ? "" : p.toFixed(2) + " CHF";
                        }
                    },
                },
                {
                    name: i18n("general.fields.available"),
                    property: "isAvailable",
                    styles: { width: "8%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("stock.fields.safetyStock"),
                    property: "stock.safetyStock",
                    styles: { width: "12%" },
                    formatter: (m, p) => (p === undefined ? "0" : p),
                },
                {
                    name: i18n("stock.fields.stock"),
                    property: "stock",
                    styles: { width: "20%" },
                    formatter: (m) => {
                        const editor = new StockTableEditor(m.id, m.isAvailable, m.stock);
                        editor.onSaved = (m) => {
                            const row = this.dataTable.findRowByModel(m);
                            if (row !== undefined)
                                row.find("> td:eq(3)").html(m.isAvailable ? i18n("general.yes") : i18n("general.no"));
                        };
                        this.editors.push(editor);

                        return editor.$element;
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            beforeTableClears: () => {
                //clear old editors
                for (const i in this.editors) this.editors[i].destroy();

                this.editors = [];
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "internalName",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<IStockTableViewArticle>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            {
                getData: (m) => this.dao.getStockArticles(m),
            },
            tableConfig
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }
}
